/**
 * Utility functions for formatting resource names in experimental applications
 */

/**
 * Default GitHub organization for experimental applications
 */
export const GITHUB_ORG = 'comcast-enterprise';

/**
 * Environment suffix for CNAP tenancies
 */
export const CNAP_ENV_SUFFIX = '-exp';

/**
 * Slugifies an application name for use in resource names
 * Converts to lowercase and replaces all non-alphanumeric characters (except hyphens) with hyphens
 * @param name - Application name to slugify
 * @returns Slugified name
 * @example
 * slugifyAppName('My Cool App') // returns 'my-cool-app'
 * slugifyAppName('Test@App#123') // returns 'test-app-123'
 */
export function slugifyAppName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9-]/g, '-');
}

/**
 * Formats a GitHub repository name for an experimental application
 * @param appName - Application name
 * @returns Full repository name in org/repo format
 * @example
 * formatGitHubRepoName('My App') // returns 'comcast-devx-discover/my-app'
 */
export function formatGitHubRepoName(appName: string): string {
  return `${GITHUB_ORG}/${slugifyAppName(appName)}`;
}

/**
 * Formats a CNAP tenancy name for an experimental application
 * @param appName - Application name
 * @returns Tenancy name with environment suffix
 * @example
 * formatCnapTenancyName('My App') // returns 'my-app-exp'
 */
export function formatCnapTenancyName(appName: string): string {
  return `${slugifyAppName(appName)}${CNAP_ENV_SUFFIX}`;
}

/**
 * Extracts the GitHub slug (org/repo) from a GitHub URL
 * @param url - GitHub URL like "https://github.com/comcast-devx-discover/my-app"
 * @returns The org/repo slug like "comcast-devx-discover/my-app" or undefined
 * @example
 * extractGitHubSlug('https://github.com/comcast-devx-discover/my-app')
 * // returns 'comcast-devx-discover/my-app'
 */
export function extractGitHubSlug(url?: string): string | undefined {
  if (!url) return undefined;

  try {
    const match = url.match(/github\.com\/([^/]+\/[^/]+)/);
    return match ? match[1] : undefined;
  } catch {
    return undefined;
  }
}
